import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, MapPin } from "lucide-react";

function formatGameDate(iso) {
  if (!iso) return "TBD";
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase(),
      date: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    };
  } catch {
    return { day: "", date: "TBD", time: "" };
  }
}

export default function ScheduleSection() {
  const [data, setData] = useState({ angels: [], ducks: [] });
  const [team, setTeam] = useState("angels");

  useEffect(() => {
    api.get("/schedule").then((r) => setData({ angels: r.data.angels || [], ducks: r.data.ducks || [] })).catch(() => {});
  }, []);

  const games = team === "angels" ? data.angels : data.ducks;
  const teamColor = team === "angels" ? "text-halo-red border-halo-red" : "text-[#F47A38] border-[#F47A38]";

  return (
    <section className="bg-white py-16 px-6 md:px-12 lg:px-16 border-t border-border" data-testid="schedule-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-halo-red font-sports uppercase tracking-[0.3em] text-xs mb-3 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" strokeWidth={2.5} />
              Upcoming Games
            </div>
            <h2 className="font-sports uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-halo-navy">
              Next <span className="text-halo-red">Up</span>
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="schedule-tab-angels"
              onClick={() => setTeam("angels")}
              className={`px-5 py-3 font-sports uppercase tracking-wider text-xs border-2 transition-colors ${
                team === "angels"
                  ? "bg-halo-red border-halo-red text-white"
                  : "border-halo-red/30 text-halo-red hover:border-halo-red"
              }`}
            >
              ⚾ Angels
            </button>
            <button
              data-testid="schedule-tab-ducks"
              onClick={() => setTeam("ducks")}
              className={`px-5 py-3 font-sports uppercase tracking-wider text-xs border-2 transition-colors ${
                team === "ducks"
                  ? "bg-[#F47A38] border-[#F47A38] text-white"
                  : "border-[#F47A38]/30 text-[#F47A38] hover:border-[#F47A38]"
              }`}
            >
              🏒 Ducks
            </button>
          </div>
        </div>

        {games.length === 0 ? (
          <div className="border-2 border-dashed border-halo-navy/20 p-10 text-center font-editorial italic text-halo-navy/60">
            No {team === "angels" ? "Angels" : "Ducks"} games scheduled in the next few weeks. Check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {games.slice(0, 8).map((g, i) => {
              const dt = formatGameDate(g.date);
              return (
                <div
                  key={`${team}-${i}`}
                  data-testid={`schedule-game-${team}-${i}`}
                  className={`bg-halo-cream border-l-4 ${teamColor.split(" ")[1]} p-5 hover:-translate-y-1 transition-transform`}
                >
                  <div className={`font-sports uppercase tracking-widest text-[10px] ${teamColor.split(" ")[0]}`}>
                    {dt.day} · {dt.date}
                  </div>
                  <div className="font-sports uppercase tracking-tight text-2xl text-halo-navy mt-2 leading-tight">
                    {g.home ? "vs" : "@"} {g.opponent}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-[11px] font-body text-halo-navy/60">
                    <span>{dt.time}</span>
                    <span className={`font-sports uppercase tracking-widest text-[10px] px-2 py-0.5 ${
                      g.home ? "bg-halo-navy text-halo-cream" : "border border-halo-navy/30 text-halo-navy"
                    }`}>
                      {g.home ? "Home" : "Away"}
                    </span>
                  </div>
                  {g.venue && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] font-body text-halo-navy/50 truncate">
                      <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{g.venue}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
